import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { LFOfflineOrder, SAVE_FORM_RESULT, StyleHelper } from '@lfw/roesberg.liveforms.core';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, firstValueFrom, switchMap } from 'rxjs';
import { routeOfflineOrders } from '../../../liveformsweb.routes';
import { NetworkService } from '../../../network.service';
import { OpenInNewTabDirective } from '../../../route/open-in-new-tab.directive';
import { ConfirmDialogComponent } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';
import { ErrorHandlers } from '../../../shared/helpers/error-handlers';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { OfflineFormsClient } from '../offline-forms.client';
import { OfflineFormsProvider } from '../offline-forms.provider';

@Component({
  selector: 'lfw-offline-order-list-item',
  templateUrl: './offline-order-list-item.component.html',
  styleUrls: ['./offline-order-list-item.component.scss'],
  imports: [RouterModule, MatIcon, MatIconButton, OpenInNewTabDirective, MatTooltipModule, MatMenuModule, TranslatePipe]
})
export class OfflineOrderListItemComponent {
  readonly offlineOrder = input.required<LFOfflineOrder>();

  private readonly client = inject(OfflineFormsClient);
  private readonly dialog = inject(MatDialog);
  private readonly formProvider = inject(OfflineFormsProvider);
  private readonly errorHandling = inject(ErrorHandlerService);
  private readonly networkState = toSignal(inject(NetworkService).state$);

  readonly isOffline = computed(() => this.networkState() === 'offline');
  readonly isConflicted = computed(() => this.client.isConflictedOrder(this.offlineOrder().id));

  get orderLink(): string[] {
    return ['/', routeOfflineOrders, this.offlineOrder().form.order.id];
  }

  get userCanCancel(): boolean {
    return false;
  }

  getOrderStateText(): string {
    return this.offlineOrder()?.form.order.orderState.text ?? '';
  }

  getSubStateText(): string {
    return this.offlineOrder()?.form.order.orderSubState?.text ?? '';
  }

  getScenarioText(): string {
    return this.offlineOrder()?.form.order.scenario?.name ?? '';
  }

  getOrderStateStyle(): string {
    return (
      StyleHelper.getColorStyle(
        this.offlineOrder()?.form.order.orderState.backgroundColor ?? '',
        this.offlineOrder()?.form.order.orderState.foregroundColor ?? ''
      ) ?? ''
    );
    //return this.offlineOrder()?.form.order.orderState.getStyle() ?? '';
  }

  getSubStateStyle(): string {
    return (
      StyleHelper.getColorStyle(
        this.offlineOrder()?.form.order.orderSubState?.backgroundColor ?? '',
        this.offlineOrder()?.form.order.orderSubState?.foregroundColor ?? ''
      ) ?? ''
    );
  }

  getScenarioStyle(): string {
    return StyleHelper.getColorStyle(this.offlineOrder()?.form.order.scenario?.color ?? '', '000000') ?? '';
  }

  async discardOffline(event: Event, orderId: string): Promise<void> {
    event.stopImmediatePropagation();

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'MESSAGES.DLG_DISCARD_OFFLINE_ORDER_TITLE',
        message: 'MESSAGES.DLG_DISCARD_OFFLINE_ORDER_TEXT',
        okIsDefault: true
      }
    });

    confirmDialog.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }
      try {
        await this.client.discardOfflineOrder(orderId, this.offlineOrder().form.project.id);
      } catch (error) {
        this.dialog.open(ErrorDialogComponent, {
          data: {
            title: 'ERROR.DLG_ERROR_TITLE',
            message: this.errorHandling.getErrorMessage(error)
          }
        });
      }
    });
  }

  // ToDo: CoIT do we need this orderId here, see also discardOffline method (we assume offlineOrder is always defined when this method is called)
  checkIn(event: Event, orderId: string): void {
    event.stopImmediatePropagation();
    if (!this.offlineOrder()) {
      return;
    }

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'MESSAGES.DLG_CHECK_IN_OFFLINE_ORDER_TITLE',
        message: 'MESSAGES.DLG_CHECK_IN_OFFLINE_ORDER_TEXT',
        okIsDefault: true
      }
    });

    confirmDialog.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.checkInOrder(orderId);
      }
    });
  }

  async checkInOrder(orderId: string): Promise<void> {
    if (!this.offlineOrder()) {
      return;
    }
    const offOrder = this.offlineOrder();

    this.formProvider
      .checkInOfflineOrder(offOrder)
      .pipe(
        switchMap(async (result) => {
          if (result === SAVE_FORM_RESULT.SAVED_ONLINE || result === SAVE_FORM_RESULT.FINISHED) {
            await firstValueFrom(this.client.removeOfflineOrderFromDb(orderId));
            return result;
          }

          if (result === SAVE_FORM_RESULT.FINISH_FAILED) {
            await firstValueFrom(this.client.removeOfflineOrderFromDb(orderId));

            // ToDO Inform user that he to finish the order online manually
            return result;
          }
          // ToDO CoIt:
          // this is possibly SAVE_FORM_RESULT.FAILED
          // we have to inform user about error
          return result;
        }),
        catchError((error) => {
          // display error message here if needed
          // ToDO CoIt: please check error handling here  => probably error message saving failed here
          return ErrorHandlers.errorHandler(error);
        })
      )
      .subscribe();
  }
}
